import type {
  AddOrganisationUserResponse,
  AdminOrganisationPendingInvite,
  AdminOrganisationPendingInvitesResponse,
} from "@/store/api/endpoints/organisationMembershipApi";
import type { AdminV2CrmUsersListResponse } from "@/types/adminV2";
import type { ApiError } from "@/types/api";

export const ORGANISATION_INVITE_DISPLAY_ID =
  "00000000-0000-4000-8000-000000000833";
export const ORGANISATION_INVITE_DISPLAY_PARAM = "organisationInviteFixture";
export const ORGANISATION_INVITE_DISPLAY_PATH = `/organisations/${ORGANISATION_INVITE_DISPLAY_ID}?tab=users&${ORGANISATION_INVITE_DISPLAY_PARAM}=failed-delivery`;
export const ORGANISATION_INVITE_DISPLAY_PERSON = {
  firstName: "Riley",
  lastName: "Example",
  email: "failed.delivery.warning@example.invalid",
  role: "team_member",
} as const;

/** Exact local diagnostic selector. No storage, real identity or API override. */
export const isOrganisationInviteDisplayTarget = (href: string): boolean => {
  try {
    const url = new URL(href);
    return (
      url.protocol === "http:" &&
      ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname) &&
      !url.username &&
      !url.password &&
      !url.hash &&
      url.pathname === `/organisations/${ORGANISATION_INVITE_DISPLAY_ID}` &&
      url.searchParams.size === 2 &&
      url.searchParams.getAll("tab").length === 1 &&
      url.searchParams.get("tab") === "users" &&
      url.searchParams.getAll(ORGANISATION_INVITE_DISPLAY_PARAM).length === 1 &&
      url.searchParams.get(ORGANISATION_INVITE_DISPLAY_PARAM) ===
        "failed-delivery"
    );
  } catch {
    return false;
  }
};

type FixtureResult = { data: unknown } | { error: ApiError };
interface FixtureRequest {
  url: string;
  method?: string;
  data?: unknown;
  params?: unknown;
}

const blocked = (): FixtureResult => ({
  error: {
    status: 400,
    message: "This request is unavailable in the local display fixture.",
    data: { errorCode: "LOCAL_DISPLAY_FIXTURE_ONLY" },
  },
});

/**
 * Closed in-memory transport for the real membership endpoints. Once selected,
 * leaving the fixture cannot fall through to a live request in this document.
 * Reopening/reloading starts a new synthetic attempt; there is no persistence.
 * The existing route permission check alone can read /me from its governed
 * loopback backend. This fixture never supplies an identity or permissions.
 */
export const createOrganisationInviteDisplayFixture = () => {
  let selected = false;
  let pending = false;
  const invite: AdminOrganisationPendingInvite = {
    ...ORGANISATION_INVITE_DISPLAY_PERSON,
    id: "00000000-0000-4000-8000-000000000834",
    createdAt: "2026-10-19T00:00:00.000Z",
    expiresAt: "2026-10-26T00:00:00.000Z",
    state: "pending",
  };

  return (
    request: FixtureRequest,
    context: { href: string; development: boolean; apiUrl?: string },
  ): FixtureResult | null => {
    let requested = false;
    try {
      requested = new URL(context.href).searchParams.has(
        ORGANISATION_INVITE_DISPLAY_PARAM,
      );
    } catch {
      return selected ? blocked() : null;
    }
    if (!selected && !requested) return null;
    selected = true;
    if (
      !context.development ||
      !isOrganisationInviteDisplayTarget(context.href)
    )
      return blocked();

    const method = (request.method ?? "GET").toUpperCase();
    const root = `/admin/organisations/${ORGANISATION_INVITE_DISPLAY_ID}`;
    const params = request.params;
    // CRITICAL: SLK-31 / PD-178d role prefill reads the existing-member email
    // before an invite. The local display fixture must answer only its exact
    // synthetic lookup; it must never forward a CRM request to a live API.
    if (
      method === "GET" &&
      request.url === "/admin/v2/crm/users" &&
      request.data === undefined &&
      typeof params === "object" &&
      params !== null &&
      !Array.isArray(params) &&
      Object.keys(params).length === 5 &&
      Object.keys(params).every((key) =>
        ["organisationId", "filters", "matchLogic", "limit", "offset"].includes(
          key,
        ),
      ) &&
      "organisationId" in params &&
      params.organisationId === ORGANISATION_INVITE_DISPLAY_ID &&
      "filters" in params &&
      params.filters ===
        encodeURIComponent(
          JSON.stringify([
            {
              id: "existing-member-email",
              category: "user",
              field: "user_email",
              condition: "equals",
              value: invite.email,
            },
          ]),
        ) &&
      "matchLogic" in params &&
      params.matchLogic === "all" &&
      "limit" in params &&
      params.limit === 2 &&
      "offset" in params &&
      params.offset === 0
    ) {
      const data: AdminV2CrmUsersListResponse = {
        users: [],
        pagination: { limit: 2, offset: 0, total: 0, hasMore: false },
      };
      return { data };
    }
    if (request.params && Object.keys(request.params).length > 0)
      return blocked();
    if (method === "GET" && request.url === "/admin/v2/me" && !request.data) {
      try {
        const api = new URL(context.apiUrl ?? "");
        if (
          api.protocol === "http:" &&
          ["localhost", "127.0.0.1", "[::1]"].includes(api.hostname) &&
          !api.username &&
          !api.password &&
          !api.search &&
          !api.hash
        )
          return null;
      } catch {
        /* An unqualified identity target remains blocked. */
      }
      return blocked();
    }
    // All other methods, IDs, paths or params return a local error. The
    // component supplies its empty member-list props; no real CRM record is read.
    if (method === "GET" && request.url === `${root}/user-invites`) {
      const data: AdminOrganisationPendingInvitesResponse = {
        invites: pending ? [{ ...invite }] : [],
      };
      return { data };
    }
    if (method !== "POST" || request.url !== `${root}/users`) return blocked();
    const body = request.data;
    if (
      typeof body !== "object" ||
      body === null ||
      Object.keys(body).length !== 4 ||
      !("firstName" in body) ||
      body.firstName !== invite.firstName ||
      !("lastName" in body) ||
      body.lastName !== invite.lastName ||
      !("email" in body) ||
      body.email !== invite.email ||
      !("role" in body) ||
      body.role !== invite.role ||
      pending
    )
      return blocked();
    pending = true;
    // Same JSON contract as the route's 201 response, consumed by RTK and the
    // actual submit handler. This is no HTTP/provider/delivery acceptance.
    const data: AddOrganisationUserResponse = {
      type: "invited",
      role: "team_member",
      emailDelivery: { status: "failed" },
    };
    return { data };
  };
};

const handleFixture = createOrganisationInviteDisplayFixture();

export const handleOrganisationInviteDisplayFixture = (
  request: FixtureRequest,
): FixtureResult | null =>
  handleFixture(request, {
    href: typeof window === "undefined" ? "" : window.location.href,
    development: import.meta.env.DEV === true,
    apiUrl: import.meta.env.VITE_API_URL,
  });
